import { updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ViewDetailsModal } from '../../common/modals/ViewDetailsModal/ViewDetailsModal';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import {
  ServiceRequestTaskCardConfig,
  TCommonTaskProp,
  TIncorporationInfoTaskMetadata,
} from '../../types';
import { IncorporationDetailsModal } from './modal/IncorporationDetailsModal';
import { IncorporationDetailsSchemaFields } from './schema/incorporation-details-schema';

export const IncorporationDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const incorporationDetailsModalVisibility = useToggle();
  const viewIncorporationDetailsModalVisibility = useToggle();

  const metadata = task?.metadata as TIncorporationInfoTaskMetadata;

  const getRowsForViewDetails = () => {
    if (!metadata?.incorporationInfo) return [];
    const rows = [
      {
        label: "Entity's final name",
        value: metadata.incorporationInfo.corporationName || '',
      },
      {
        label: 'Date of incorporation',
        value: metadata.incorporationInfo.formationDate
          ? NonTimezoneSensitiveDate.fromISO8601String(
              metadata.incorporationInfo.formationDate
            ).format('MMM D, YYYY')
          : '',
      },
      {
        label: 'Total number of shares',
        value: metadata.incorporationInfo.authorizedShares?.toString() ?? '',
      },
      {
        label: 'Corporation entity number',
        value:
          metadata.incorporationInfo.uniqueIdentifierFromJurisdiction || '',
      },
      {
        label: 'Address of registered agent',
        value: metadata.incorporationInfo.registeredAgent?.address || '',
      },
    ];
    return rows;
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: metadata?.incorporationInfo ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={viewIncorporationDetailsModalVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation details updated by Commenda"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda updates details for Incorporation" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda updates details for Incorporation" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: metadata?.incorporationInfo ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={viewIncorporationDetailsModalVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation details entered"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={incorporationDetailsModalVisibility.toggle}
              >
                Start <Icons.ArrowUpRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Enter incorporation details" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowUpRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Enter incorporation details" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <IncorporationDetailsModal
        serviceRequest={serviceRequest}
        mutateServiceRequest={mutate}
        isOpen={incorporationDetailsModalVisibility.visible}
        setIsOpen={incorporationDetailsModalVisibility.hide}
        onConfirm={async (
          formData: IncorporationDetailsSchemaFields & {
            registeredAgentAddress: string;
          }
        ) => {
          await updateTask({
            id: task.id,
            data: {
              status: TaskStatus.COMPLETED,
              metadata: {
                incorporationInfo: {
                  corporationName: formData.corporationName,
                  formationDate: formData.formationDate,
                  uniqueIdentifierFromJurisdiction:
                    formData.uniqueIdentifierFromJurisdiction,
                  registeredAgent: {
                    id: formData.registeredAgentId,
                    address: formData.registeredAgentAddress,
                  },
                  authorizedShares: formData.shareCount,
                },
              },
            },
          });
        }}
      />
      {viewIncorporationDetailsModalVisibility.visible ? (
        <ViewDetailsModal
          isOpen={viewIncorporationDetailsModalVisibility.visible}
          onClose={viewIncorporationDetailsModalVisibility.hide}
          title="Incorporation details"
          content={getRowsForViewDetails()}
          className="max-w-[600px]"
        />
      ) : null}
    </div>
  );
};
